/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Theme } from "@mui/material";
import { SxProps } from "@mui/system";
import {
  GridColDef,
  GridPinnedColumnFields,
  GridPinnedRowsProp,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useIsMobile } from "hooks/useIsMobile";
import {
  ChefGroup,
  MaterialRead,
  ProductGroup,
} from "src/store/api/generatedApi";
import { TitleColumnHeader } from "../common/TitleColumnHeader";
import { AggregationCell, VisualisedCell } from "./ProductionCell";
import {
  Group,
  Measure,
  useIsStatic,
  useMixColumnWidthPx,
} from "./ProductionContext";
import { useInventoryColumns } from "./inventoryColumns";
import { ScrapRowHeader } from "./ScrapRowHeader";
import { PlanGroup, PlanMix, PlanMixes } from "./productionContent";

import { useTenantData } from "contexts/tenant";
import type { JSX } from "react";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { BaseHeatHeader, HeatHeader } from "./HeatHeader";

type RowData = Record<number, Record<number, PlanMix & { id: number }>>;

type AggregationRowId =
  | "total_mass"
  | "total_price"
  | "materials_price"
  | "electrical_energy_price"
  | "charged_price";

interface ScrapRow {
  id: number | AggregationRowId;
  scrap: MaterialRead;
  data: RowData;
}

interface Props {
  materials: MaterialRead[];
  measure: Measure;
  planGroups: PlanGroup[];
  planMixes: PlanMixes;
  grouping: Record<number, ProductGroup> | Record<number, ChefGroup>;
  steelGrades: Set<string>;
  chemistryGroups: Record<number, ChefGroup>;
  group: Group;
  period: number;
  sx: SxProps<Theme>;
}

const useLayout = (
  inventoryColumns: GridColDef<ScrapRow>[],
  materials: MaterialRead[],
  measure: Measure,
  planGroups: PlanGroup[],
  planMixes: PlanMixes,
  grouping: Record<number, ProductGroup> | Record<number, ChefGroup>,
  steelGrades: Set<string>,
  chemistryGroups: Record<number, ChefGroup>,
  group: Group,
  period: number,
  isMobile: boolean,
  showChargedCost: boolean,
  isStatic: boolean
) => {
  const { t } = useTenantTranslation();
  const mixColumnWidthPx = useMixColumnWidthPx();
  const {
    basket_tab_type: basketTabType,
    small_mass_per_mass: smallMassPerMass,
  } = useTenantData();
  const unit = useUnitsFormatter(true);
  const massUnit = measure === Measure.SmallMass ? "small_mass" : "mass";

  const groupings: [ProductGroup, PlanGroup][] = planGroups
    .map((group): [ProductGroup, PlanGroup] => [
      grouping[group.groupId]!,
      group,
    ])
    .filter(
      ([grouping]) =>
        steelGrades.size === 0 ||
        grouping.steel_grades.some(({ name }) => steelGrades.has(name))
    );

  const scrapColumn: GridColDef<ScrapRow> = {
    field: "scrap",
    sortable: false,
    minWidth: isMobile ? 120 : 200,
    flex: isMobile ? 1 : 0,
    display: "flex",
    renderHeader: () => <TitleColumnHeader title={t("scrap")} />,
    headerClassName: "group--header",
    cellClassName: "group--header",
    valueGetter: (_, { scrap, id }) => {
      switch (id) {
        case "total_mass":
          return `${t("totalMass")}${unit(massUnit)}`;
        case "total_price":
          return `${t("totalPrice")}${unit("cost")}}`;
        case "materials_price":
          return `${t("totalPrice")}${unit("cost")}}`;
        case "electrical_energy_price":
          return `${t("totalPrice")}${unit("cost")}}`;
        case "charged_price":
          return `${t("chargedPrice")}${unit("specific_cost")}`;
        default: {
          return scrap.name;
        }
      }
    },
    renderCell: ({
      row: {
        scrap: { name, id: material_id },
        id,
      },
    }) => {
      return <ScrapRowHeader rowId={id} id={material_id} name={name} />;
    },
  };

  const groupColumns: GridColDef<ScrapRow>[] = groupings.map(
    (
      [
        { id, name },
        {
          heats,
          mixId,
          mixNumber,
          numBaskets,
          multipleMixes,
          groupId,
          customerMixId,
        },
      ],
      index
    ) => {
      return {
        field: "group_" + index.toString(),
        sortable: false,
        width: isMobile ? undefined : mixColumnWidthPx,
        minWidth: isMobile ? 90 : 150,
        flex: isMobile ? 1 : 0,
        headerClassName: "group--header",

        valueGetter: (_, { data }) => {
          if (id !== null && id !== undefined && data[id]?.[mixId]) {
            const { id: materialId, materials, total } = data[id][mixId];
            const value = materials[materialId];
            if (value) {
              switch (measure) {
                case Measure.Percentage: {
                  return value / total;
                }
                case Measure.Mass: {
                  return value;
                }
                case Measure.SmallMass: {
                  return value * smallMassPerMass;
                }
              }
            } else {
              return null;
            }
          }
        },
        renderCell: ({ row: { data, id: rowId } }): JSX.Element | null => {
          if (id != null && data[id]?.[mixId]) {
            const {
              materials,
              total,
              id: materialId,
              maximum,
              total_price,
              materials_price,
              electrical_energy_price,
              charged_price,
            } = data[id][mixId];

            switch (rowId) {
              case "total_mass": {
                return (
                  <AggregationCell
                    value={
                      measure === Measure.SmallMass
                        ? total * smallMassPerMass
                        : total
                    }
                    variant={
                      measure === Measure.SmallMass ? "smallMass" : "mass"
                    }
                  />
                );
              }
              case "total_price": {
                return <AggregationCell value={total_price} variant="price" />;
              }
              case "materials_price": {
                return (
                  <AggregationCell value={materials_price} variant="price" />
                );
              }
              case "electrical_energy_price": {
                return (
                  <AggregationCell
                    value={electrical_energy_price}
                    variant="price"
                  />
                );
              }
              case "charged_price": {
                return (
                  <AggregationCell value={charged_price} variant="price" />
                );
              }
              default: {
                const value = materials[materialId];
                return value == null || value === 0.0 ? null : (
                  <VisualisedCell
                    value={value}
                    total={total}
                    maximum={maximum}
                  />
                );
              }
            }
          } else {
            return null;
          }
        },
        renderHeader: () => {
          if (isStatic) {
            return (
              <BaseHeatHeader
                heats={heats}
                mixNumber={mixNumber}
                multipleMixes={multipleMixes}
                name={name}
                groupId={groupId}
                groupOption={group}
                numBaskets={null}
                customerMixId={customerMixId}
                headerType="column"
              />
            );
          }
          const chemistryGroup = chemistryGroups[groupId];
          if (chemistryGroup) {
            switch (basketTabType) {
              case "layering_table":
                return (
                  <HeatHeader
                    period={period}
                    heats={heats}
                    mixNumber={mixNumber}
                    multipleMixes={multipleMixes}
                    name={name}
                    groupId={groupId}
                    groupOption={group}
                    customerMixId={customerMixId}
                    chemistryGroupId={chemistryGroup.id!}
                    headerType="column"
                  />
                );
              case "basket_plot":
                return (
                  <HeatHeader
                    heats={heats}
                    mixNumber={mixNumber}
                    multipleMixes={multipleMixes}
                    name={name}
                    groupId={groupId}
                    groupOption={group}
                    customerMixId={customerMixId}
                    numBaskets={numBaskets}
                    headerType="column"
                  />
                );
            }
          } else {
            return (
              <BaseHeatHeader
                heats={heats}
                mixNumber={mixNumber}
                multipleMixes={multipleMixes}
                name={name}
                groupId={groupId}
                groupOption={group}
                numBaskets={null}
                customerMixId={customerMixId}
                headerType="column"
              />
            );
          }
        },
      };
    }
  );

  const materialRows: ScrapRow[] = materials.map((material, index) => {
    const data: RowData = groupings.reduce<RowData>((row, [, group]) => {
      row[group.groupId] ??= {};

      if (!row[group.groupId]![group.mixId]) {
        row[group.groupId]![group.mixId] = {
          ...planMixes[group.groupId]![group.mixId]!,
          id: material.id,
        };
        return row;
      }

      row[group.groupId]![group.mixId] = {
        ...planMixes[group.groupId]![group.mixId]!,
        id: material.id,
      };

      return row;
    }, {});
    return {
      id: index,
      scrap: material,
      data,
    };
  });

  const columns: GridColDef<ScrapRow>[] = [
    scrapColumn,
    ...groupColumns,
    ...inventoryColumns,
  ];

  // Only show the price breakdown if the electrical energy price is nonzero for at least one mix
  const splitPriceIntoComponents =
    materialRows.filter(
      (row) =>
        Object.values(row.data).filter(
          (groupMixes) =>
            Object.values(groupMixes).filter(
              (mix) => mix.electrical_energy_price > 0
            ).length > 0
        ).length > 0
    ).length > 0;

  const firstRow = materialRows[0];
  const aggregationRows: ScrapRow[] = firstRow
    ? [
        {
          id: "total_mass",
          scrap: firstRow.scrap,
          data: firstRow.data,
        },
        ...(splitPriceIntoComponents
          ? [
              {
                id: "materials_price" as AggregationRowId,
                scrap: firstRow.scrap,
                data: firstRow.data,
              },
              {
                id: "electrical_energy_price" as AggregationRowId,
                scrap: firstRow.scrap,
                data: firstRow.data,
              },
            ]
          : []),
        {
          id: "total_price",
          scrap: firstRow.scrap,
          data: firstRow.data,
        },
        ...(showChargedCost
          ? [
              {
                id: "charged_price" as AggregationRowId,
                scrap: firstRow.scrap,
                data: firstRow.data,
              },
            ]
          : []),
      ]
    : [];

  // Static renders have no pinned rows, so the aggregation rows join the normal
  // rows and appear as ordinary bottom rows in the screenshot.
  const rows: ScrapRow[] = isStatic
    ? [...materialRows, ...aggregationRows]
    : materialRows;
  const pinnedRows: GridPinnedRowsProp<ScrapRow> = {
    bottom: isStatic ? [] : aggregationRows,
  };

  const pinnedColumns: GridPinnedColumnFields = {
    left: ["scrap"],
  };

  return {
    rows,
    columns,
    pinnedColumns,
    pinnedRows,
    rowHeight: 32,
    columnHeaderHeight: 70,
  };
};

export const HeatXScrapYTable = ({
  materials,
  measure,
  planGroups,
  planMixes,
  grouping,
  steelGrades,
  chemistryGroups,
  group,
  period,
  sx,
}: Props) => {
  const isStatic = useIsStatic();
  const isMobile = useIsMobile() && !isStatic;
  const showChargedCost = useIsBehindFeatureFlag("show_charged_cost");
  const inventoryColumns = useInventoryColumns<ScrapRow>();
  const {
    pinnedColumns,
    pinnedRows,
    columns,
    rows,
    columnHeaderHeight,
    rowHeight,
  } = useLayout(
    inventoryColumns,
    materials,
    measure,
    planGroups,
    planMixes,
    grouping,
    steelGrades,
    chemistryGroups,
    group,
    period,
    isMobile,
    showChargedCost,
    isStatic
  );

  return (
    <HotkeyCompliantDataGrid
      sx={sx}
      pinnedColumns={pinnedColumns}
      columns={columns}
      pinnedRows={pinnedRows}
      rows={rows}
      columnHeaderHeight={columnHeaderHeight}
      rowHeight={rowHeight}
      cellSelection={!isStatic}
      autoHeight={isStatic}
      disableVirtualization={isStatic}
      ignoreValueFormatterDuringExport
    />
  );
};
